import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Shield } from 'lucide-react';
import { setAnalyticsConsent, hasAnalyticsConsent } from '../hooks/useAnalytics';

// Cookie consent ONLY shows on homepage
// Once dismissed or interacted with, it should never show again except on homepage return
const HOMEPAGE_PATH = '/';

const CookieConsent = () => {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Only show on exact homepage path
  const isHomepage = location.pathname === HOMEPAGE_PATH;

  useEffect(() => {
    // ONLY show on homepage
    if (!isHomepage) {
      setShowBanner(false);
      return;
    }
    
    // Check if user has already made a choice (accepted or declined)
    const consent = document.cookie.includes('bc_analytics_consent=');
    if (!consent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isHomepage, location.pathname]);

  const handleAccept = () => {
    setAnalyticsConsent(true);
    setShowBanner(false);
  };

  const handleDecline = () => {
    setAnalyticsConsent(false);
    setShowBanner(false);
  };

  if (!showBanner || !isHomepage) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Main Banner */}
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                <Cookie className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  We value your privacy
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  We use cookies and similar technologies to improve your experience, analyze site traffic, 
                  and understand how you interact with our menu and ordering system. This helps us serve you better.
                </p>
                
                {/* Details Section */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mb-4 overflow-hidden"
                    >
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <Shield className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm">What we collect</h4>
                            <ul className="text-xs text-gray-600 dark:text-gray-400 mt-1 space-y-1">
                              <li>• Pages you visit and time spent</li>
                              <li>• Buttons and links you click</li>
                              <li>• Items added to your cart</li>
                              <li>• Order completion data</li>
                              <li>• QR code scan sources</li>
                            </ul>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm">How we use it</h4>
                            <ul className="text-xs text-gray-600 dark:text-gray-400 mt-1 space-y-1">
                              <li>• Improve our menu and service</li>
                              <li>• Understand popular items</li>
                              <li>• Optimize ordering experience</li>
                              <li>• Measure marketing effectiveness</li>
                            </ul>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-500 italic">
                          We never sell your personal data to third parties. Data is stored securely and used only to improve your experience.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={handleAccept}
                    className="px-5 py-2.5 bg-[#2D5016] hover:bg-[#3d6b1e] text-white text-sm font-medium rounded-lg transition-colors"
                    data-testid="cookie-accept-btn"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleDecline}
                    className="px-5 py-2.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
                    data-testid="cookie-decline-btn"
                  >
                    Decline
                  </button>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    {showDetails ? 'Hide details' : 'Learn more'}
                  </button>
                </div>
              </div>
              
              <button
                onClick={handleDecline}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
